import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import {
  queueSettingsApi,
  queueSettingsQueryKeys,
  type QueueCallSettings,
} from "../../shared/api/queueSettings";
import {
  branchesApi,
  branchesQueryKeys,
  ACTIVE_BRANCH_KEY,
} from "../../shared/api/branches";
import { apiError } from "../../shared/api/client";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleRow } from "./components/ToggleRow";

/**
 * Cài đặt → Vận hành → Gọi số & màn hình chờ (Redmine #18575, design §22). Bật/tắt tự động gọi
 * bệnh nhân kế tiếp khi hoàn tất khám, và số bệnh nhân "Mời chuẩn bị" trên màn hình phòng. Cấu hình
 * theo chi nhánh đang chọn.
 */
export function QueueCallSettingsPage() {
  const qc = useQueryClient();

  // Chi nhánh đang chọn (giống BranchSwitcher): localStorage → chi nhánh đầu tiên.
  const branchesQuery = useQuery({
    queryKey: branchesQueryKeys.all(),
    queryFn: () => branchesApi.list(),
    staleTime: 5 * 60_000,
  });
  const branchId = useMemo(() => {
    const active = localStorage.getItem(ACTIVE_BRANCH_KEY);
    if (active) return active;
    const first = (branchesQuery.data ?? []).find((b) => b.status === "ACTIVE");
    return first?.id ?? "";
  }, [branchesQuery.data]);

  const q = useQuery({
    queryKey: queueSettingsQueryKeys.of(branchId),
    queryFn: () => queueSettingsApi.get(branchId),
    enabled: !!branchId,
  });

  const [form, setForm] = useState<QueueCallSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    if (q.data) setForm(q.data);
  }, [q.data]);

  const save = useMutation({
    mutationFn: (next: QueueCallSettings) =>
      queueSettingsApi.update(branchId, next),
    onSuccess: (data) => {
      qc.setQueryData(queueSettingsQueryKeys.of(branchId), data);
      setForm(data);
      setError(null);
      setSavedAt(Date.now());
    },
    onError: (e) => setError(apiError(e).detail),
  });

  return (
    <div>
      <PageHeader
        title="Gọi số & màn hình chờ"
        description="Cấu hình luồng gọi bệnh nhân tại phòng khám cho chi nhánh đang chọn."
      />

      {branchesQuery.isLoading || q.isLoading || !form ? (
        <p className="text-sm text-muted-foreground">Đang tải…</p>
      ) : (
        <Card className="max-w-2xl">
          <CardContent className="divide-y p-5">
            <ToggleRow
              label="Tự động gọi bệnh nhân kế tiếp khi hoàn tất khám"
              description="Khi bác sĩ ký lần khám, hệ thống tự gọi bệnh nhân đủ điều kiện kế tiếp vào phòng đó. Nếu không còn ai chờ thì KHÔNG tự tăng số."
              checked={form.autoCallNext}
              onChange={(v) => setForm({ ...form, autoCallNext: v })}
            />
            <div className="flex items-center justify-between gap-4 py-4">
              <div>
                <Label htmlFor="prep">Số bệnh nhân "Mời chuẩn bị"</Label>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Số STT hiển thị ở mục "Mời chuẩn bị" trên màn hình phòng (layout C).
                </p>
              </div>
              <Input
                id="prep"
                type="number"
                min={0}
                className="w-24"
                value={form.prepareGroupSize}
                onChange={(e) =>
                  setForm({
                    ...form,
                    prepareGroupSize: Math.max(
                      0,
                      Number.parseInt(e.target.value || "0", 10)
                    ),
                  })
                }
              />
            </div>
          </CardContent>
        </Card>
      )}

      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

      <div className="mt-4 flex items-center gap-3">
        <Button
          type="button"
          disabled={!form || save.isPending}
          onClick={() => form && save.mutate(form)}
        >
          {save.isPending ? "Đang lưu…" : "Lưu"}
        </Button>
        {savedAt > 0 && !save.isPending && (
          <span className="inline-flex items-center gap-1 text-sm text-emerald-600">
            <Check size={16} /> Đã lưu
          </span>
        )}
      </div>
    </div>
  );
}
